import { inject, Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType, rootEffectsInit } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, tap } from 'rxjs';

import { AssistantSizeStorageService } from '../assistant-size-storage.service';

import { AssistantActions } from './assistant.actions';
import { AssistantSelectors } from './assistant.selectors';
import { AssistantSize } from './assistant.state';


/**
 * Verbindet die Panel-Größe mit dem lokalen Speicher des Browsers.
 *
 * Über Effects und nicht über den Panel-Service, weil der nur im
 * Standalone-Bereich lebt, das Merken aber an der Sitzung hängen soll.
 */
@Injectable()
export class AssistantEffects {

  private readonly _actions$ = inject(Actions);

  private readonly _store = inject(Store);

  private readonly _storage = inject(AssistantSizeStorageService);

  /** Ohne gemerkten Wert bleibt der Initialwert stehen. */
  restore$ = createEffect(
    () => this._actions$.pipe(
      ofType(rootEffectsInit),
      map(() => this._storage.read()),
      filter((size): size is AssistantSize => !!size),
      map(size => AssistantActions.restoreSize({ size })),
    ),
  );

  /**
   * An der Action und nicht am Größen-Zustand, damit nur eine Umschaltung des
   * Nutzers schreibt: Ein Abonnement auf den Zustand würde auch den gerade
   * gelesenen Wert aus `restoreSize` gleich wieder zurückschreiben.
   *
   * `concatLatestFrom` liest den Wert nach dem Reducer-Lauf, also die neue
   * Größe.
   */
  persist$ = createEffect(
    () => this._actions$.pipe(
      ofType(AssistantActions.toggleSize),
      concatLatestFrom(() => this._store.select(AssistantSelectors.size)),
      tap(([, size]) => this._storage.write(size)),
    ),
    { dispatch: false },
  );

}
